import { emptySjaHazard, normalizeSjaHazards } from "./sjaHazards";
import { normalizeSjaApproval } from "./sjaApprovalUtils";

export const RISK_LEVELS = ["lav", "moderat", "hoy", "kritisk"];

export const RISK_CATEGORIES = [
  "sikkerhet",
  "organisatorisk",
  "psykososialt",
  "fysisk",
  "kjemisk",
  "ergonomisk",
  "miljo",
];

export const RISK_STATUSES = [
  "suggested",
  "draft",
  "in_progress",
  "assessed",
  "skipped",
  "closed",
];

export const SJA_WORK_TYPES = [
  "routine",
  "non_routine",
  "maintenance",
  "construction",
  "emergency",
  "special",
];

export const SJA_STATUSES = ["draft", "pending_approval", "approved", "in_use", "archived"];

export const CLIMATE_CATEGORIES = [
  "physical_risks",
  "transition_risks",
  "regulatory_risks",
  "market_risks",
  "reputational_risks",
];

export const CLIMATE_HORIZONS = ["short_term", "medium_term", "long_term", "very_long_term"];

export const RISK_PATHS = {
  hub: "/dashboard/hse-ks/risk",
  register: "/dashboard/hse-ks/risk/register",
  campaign: (id) => `/dashboard/hse-ks/risk/campaigns/${id}`,
  matrix: "/dashboard/hse-ks/risk/matrix",
  sja: "/dashboard/hse-ks/risk/sja",
  climate: "/dashboard/hse-ks/risk/climate",
  chemical: "/dashboard/hse-ks/risk/chemical-inventory",
  protective: "/dashboard/hse-ks/risk/protective-equipment",
};

export function riskShowBackTo(record) {
  const campaignId = record?.hse_risk_campaign_id || record?.campaign_id;
  if (campaignId) return RISK_PATHS.campaign(campaignId);
  return RISK_PATHS.register;
}

export const RISK_TABS = [
  { id: "campaigns", path: RISK_PATHS.hub, labelKey: "hse.risk.nav.campaigns" },
  { id: "register", path: RISK_PATHS.register, labelKey: "chemical.inventory.risk_tab.register" },
  { id: "matrix", path: RISK_PATHS.matrix, labelKey: "chemical.inventory.risk_tab.matrix" },
  { id: "sja", path: RISK_PATHS.sja, labelKey: "chemical.inventory.risk_tab.sja" },
  { id: "climate", path: RISK_PATHS.climate, labelKey: "chemical.inventory.risk_tab.climate" },
  { id: "chemical-inventory", path: RISK_PATHS.chemical, labelKey: "chemical.inventory" },
  { id: "protective-equipment", path: RISK_PATHS.protective, labelKey: "chemical.inventory.risk_tab.protective" },
];

export function detectRiskTab(pathname = "") {
  if (pathname.includes("/matrix")) return "matrix";
  if (pathname.includes("/sja")) return "sja";
  if (pathname.includes("/climate")) return "climate";
  if (pathname.includes("/chemical-inventory")) return "chemical-inventory";
  if (pathname.includes("/protective-equipment")) return "protective-equipment";
  if (pathname.includes("/register")) return "register";
  return "campaigns";
}

const LEVEL_ALIASES = {
  lav: "lav",
  very_low: "lav",
  verylow: "lav",
  low: "lav",
  liten: "lav",
  medium: "moderat",
  moderate: "moderat",
  moderat: "moderat",
  high: "hoy",
  hoy: "hoy",
  høy: "hoy",
  very_high: "kritisk",
  veryhigh: "kritisk",
  critical: "kritisk",
  kritisk: "kritisk",
};

const STATUS_ALIASES = {
  suggested: "suggested",
  draft: "draft",
  identified: "draft",
  in_progress: "in_progress",
  assessed: "assessed",
  mitigated: "assessed",
  monitored: "assessed",
  skipped: "skipped",
  excluded: "skipped",
  not_assessed: "skipped",
  closed: "closed",
};

export function firstPresent(record, keys, fallback = "") {
  if (!record) return fallback;
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
}

export function normalizeRiskLevel(value, fallback = "") {
  if (!value) return fallback;
  const key = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  return LEVEL_ALIASES[key] || fallback || key;
}

export function normalizeRiskStatus(value, fallback = "draft") {
  if (!value) return fallback;
  const key = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  return STATUS_ALIASES[key] || fallback;
}

export function scoreToLevel(score) {
  const n = Number(score) || 0;
  if (n <= 0) return "";
  if (n <= 4) return "lav";
  if (n <= 12) return "moderat";
  if (n <= 20) return "hoy";
  return "kritisk";
}

export function levelToScores(level) {
  switch (normalizeRiskLevel(level)) {
    case "lav":
      return { likelihood: 2, consequence_score: 2 };
    case "moderat":
      return { likelihood: 3, consequence_score: 3 };
    case "hoy":
      return { likelihood: 4, consequence_score: 4 };
    case "kritisk":
      return { likelihood: 5, consequence_score: 5 };
    default:
      return { likelihood: "", consequence_score: "" };
  }
}

export function riskScore(record = {}) {
  const likelihood = Number(record.likelihood);
  const consequence = Number(record.consequence_score ?? record.consequence);
  if (!likelihood || !consequence) return record.risk_score || 0;
  return likelihood * consequence;
}

export function needsTiltak(level) {
  return ["moderat", "hoy", "kritisk"].includes(normalizeRiskLevel(level));
}

export function isExcluded(record = {}) {
  return (
    record.skip_assessment === true ||
    record.excluded === true ||
    record.not_assessed === true ||
    normalizeRiskStatus(record.status) === "skipped"
  );
}

export function categorySlug(record = {}) {
  if (record.category && typeof record.category === "object") {
    return record.category.slug || "";
  }
  return firstPresent(record, ["category_slug", "category"], "");
}

export function categoryName(record = {}, fallback = "") {
  if (record.category_name) return record.category_name;
  if (record.category && typeof record.category === "object") {
    return record.category.name || record.category.slug || fallback;
  }
  return fallback;
}

export function emptyRiskForm(defaults = {}) {
  return {
    title: defaults.title || "",
    category: defaults.category || "",
    category_name: defaults.category_name || "",
    hse_risk_category_id: defaults.hse_risk_category_id || "",
    challenge_kind: defaults.challenge_kind || "custom",
    theme: defaults.theme || "",
    catalog_key: defaults.catalog_key || defaults.challenge_key || "",
    challenge_key: defaults.challenge_key || defaults.catalog_key || "",
    challenge_title: defaults.challenge_title || "",
    description: defaults.description || "",
    consequence: defaults.consequence || "",
    risk_level: defaults.risk_level || "",
    likelihood: defaults.likelihood || "",
    consequence_score: defaults.consequence_score || "",
    status: defaults.status || "draft",
    skip_assessment: defaults.skip_assessment || false,
    work_task: defaults.work_task || "",
    triggering_cause: defaults.triggering_cause || "",
    existing_measures: defaults.existing_measures || "",
    history: defaults.history || "",
    excluded_reason: defaults.excluded_reason || defaults.skip_reason || "",
    skip_reason: defaults.skip_reason || defaults.excluded_reason || "",
    tiltak_ids: defaults.tiltak_ids || [],
    files: defaults.files || [],
    hse_risk_campaign_id: defaults.hse_risk_campaign_id || defaults.campaign_id || "",
  };
}

export function normalizeRisk(record = {}) {
  const likelihood = firstPresent(record, ["likelihood", "probability"], "");
  const consequenceScore = firstPresent(
    record,
    ["consequence_score", "consequence_level", "impact"],
    ""
  );
  const qualitative = normalizeRiskLevel(
    firstPresent(record, ["risk_level", "level", "qualitative_level"], "")
  );
  const score =
    Number(record.risk_score) ||
    (Number(likelihood) && Number(consequenceScore)
      ? Number(likelihood) * Number(consequenceScore)
      : 0);
  const skipped = isExcluded(record);
  const status = skipped
    ? "skipped"
    : normalizeRiskStatus(record.status, qualitative || score ? "assessed" : "draft");
  const slug = categorySlug(record);

  return {
    ...emptyRiskForm(),
    id: record.id,
    title: firstPresent(record, ["title", "name"], ""),
    category: slug,
    category_name: categoryName(record),
    hse_risk_category_id: firstPresent(record, ["hse_risk_category_id"], ""),
    challenge_kind: record.challenge_kind === "common" ? "common" : "custom",
    theme: firstPresent(record, ["theme", "theme_id"], ""),
    catalog_key: firstPresent(record, ["challenge_key", "catalog_key"], ""),
    challenge_key: firstPresent(record, ["challenge_key", "catalog_key"], ""),
    challenge_title: firstPresent(record, ["challenge_title"], ""),
    description: firstPresent(record, ["description", "challenge"], ""),
    consequence:
      typeof record.consequence === "string"
        ? record.consequence
        : firstPresent(record, ["consequence_text"], ""),
    risk_level: qualitative || scoreToLevel(score),
    likelihood: likelihood === "" ? "" : Number(likelihood),
    consequence_score: consequenceScore === "" ? "" : Number(consequenceScore),
    risk_score: score,
    status,
    skip_assessment: skipped,
    excluded: skipped,
    work_task: firstPresent(record, ["work_task", "task"], ""),
    triggering_cause: firstPresent(record, ["triggering_cause", "cause"], ""),
    existing_measures: firstPresent(record, ["existing_measures", "current_controls"], ""),
    history: firstPresent(record, ["history", "background"], ""),
    excluded_reason: firstPresent(record, ["skip_reason", "excluded_reason"], ""),
    skip_reason: firstPresent(record, ["skip_reason", "excluded_reason"], ""),
    open_tiltak_count: record.open_tiltak_count || 0,
    tiltak: Array.isArray(record.tiltak)
      ? record.tiltak
      : Array.isArray(record.actions)
        ? record.actions
        : Array.isArray(record.hse_actions)
          ? record.hse_actions
          : [],
    tiltak_ids: record.tiltak_ids || [],
    files: Array.isArray(record.files)
      ? record.files
      : Array.isArray(record.attachments)
        ? record.attachments
        : [],
    project_id: record.project_id || "",
    hse_risk_campaign_id: firstPresent(record, ["hse_risk_campaign_id", "campaign_id"], ""),
    updated_at: record.updated_at,
    created_at: record.created_at,
  };
}

export function toRiskPayload(form) {
  const blankToNull = (value) => (value === "" || value == null ? null : value);
  const scores =
    form.likelihood && form.consequence_score
      ? { likelihood: Number(form.likelihood), consequence_score: Number(form.consequence_score) }
      : levelToScores(form.risk_level);
  const skipped = form.skip_assessment || form.status === "skipped" || form.status === "excluded";
  return {
    title: (form.title || "").trim(),
    description: form.description || "",
    challenge_kind: form.challenge_kind === "common" ? "common" : "custom",
    theme: blankToNull(form.theme),
    challenge_key: blankToNull(form.challenge_key || form.catalog_key),
    challenge_title: blankToNull(form.challenge_title || form.title),
    hse_risk_category_id: blankToNull(form.hse_risk_category_id),
    consequence: form.consequence || "",
    risk_level: skipped ? null : form.risk_level || null,
    probability: blankToNull(scores.likelihood),
    consequence_score: blankToNull(scores.consequence_score),
    status: skipped ? "skipped" : form.status || (form.risk_level ? "assessed" : "draft"),
    skip_assessment: skipped,
    skip_reason: form.skip_reason || form.excluded_reason || "",
    work_task: form.work_task || "",
    triggering_cause: form.triggering_cause || "",
    existing_measures: form.existing_measures || "",
    history: form.history || "",
    hse_risk_campaign_id: blankToNull(form.hse_risk_campaign_id || form.campaign_id),
  };
}

export function emptySjaForm(defaults = {}) {
  return {
    title: defaults.title || "",
    work_type: defaults.work_type || "routine",
    work_location: defaults.work_location || defaults.location || "",
    description: defaults.description || "",
    status: defaults.status || "draft",
    not_editable: defaults.not_editable === true,
    approval: defaults.approval || {
      required_roles: [],
      recorded: [],
      pending_roles: [],
      current_user_roles: [],
      current_user_can_approve: false,
      complete: false,
    },
    steps: defaults.steps || [{ step_number: 1, description: "", hazards: [emptySjaHazard()] }],
  };
}

export function normalizeSja(record = {}) {
  const steps = Array.isArray(record.steps)
    ? record.steps
    : Array.isArray(record.sja_steps)
      ? record.sja_steps
      : [];
  const status = firstPresent(record, ["status"], "draft");
  const notEditable =
    record.not_editable === true || record.editable === false || status !== "draft";
  return {
    id: record.id,
    title: firstPresent(record, ["title", "name"], ""),
    work_type: firstPresent(record, ["work_type"], "routine"),
    work_location: firstPresent(record, ["work_location", "location"], ""),
    description: firstPresent(record, ["description", "work_description"], ""),
    status,
    not_editable: notEditable,
    overall_risk_level: normalizeRiskLevel(record.overall_risk_level || record.risk_level, ""),
    approval: normalizeSjaApproval(record),
    approved_by: record.approved_by || null,
    approved_at: record.approved_at || null,
    steps: steps.length
      ? steps.map((step, index) => ({
          id: step.id,
          step_number: step.step_number || index + 1,
          description: step.description || step.title || "",
          hazards: normalizeSjaHazards(step),
        }))
      : [{ step_number: 1, description: "", hazards: [emptySjaHazard()] }],
    updated_at: record.updated_at,
    created_at: record.created_at,
  };
}

export function toSjaPayload(form) {
  return {
    title: (form.title || "").trim(),
    work_type: form.work_type || "routine",
    location: form.work_location || form.location || "",
    description: form.description || "",
    work_description: form.description || "",
    sja_steps_attributes: (form.steps || []).map((step, index) => step._destroy
      ? { id: step.id, _destroy: true }
      : {
        id: step.id || undefined,
        step_number: step.step_number || index + 1,
        description: step.description || "",
        sja_hazards_attributes: normalizeSjaHazards(step).map((hazard) => hazard._destroy
          ? { id: hazard.id, _destroy: true }
          : {
            id: hazard.id || undefined,
            hazard_type: hazard.hazard_type,
            description: hazard.description,
            likelihood: hazard.likelihood === "" ? null : Number(hazard.likelihood),
            consequence: hazard.consequence === "" ? null : Number(hazard.consequence),
            existing_measures: hazard.existing_measures,
            additional_measures: hazard.additional_measures,
          }),
      }),
  };
}

export function emptyClimateForm(defaults = {}) {
  return {
    title: defaults.title || "",
    risk_category: defaults.risk_category || "physical_risks",
    time_horizon: defaults.time_horizon || "medium_term",
    description: defaults.description || "",
    likelihood: defaults.likelihood || "",
    consequence: defaults.consequence || "",
  };
}

export function normalizeClimate(record = {}) {
  return {
    id: record.id,
    title: firstPresent(record, ["title", "name"], ""),
    risk_category: firstPresent(record, ["risk_category", "category"], "physical_risks"),
    time_horizon: firstPresent(record, ["time_horizon"], "medium_term"),
    description: firstPresent(record, ["description"], ""),
    likelihood: record.likelihood || "",
    consequence:
      typeof record.consequence === "number" ? record.consequence : record.consequence_score || "",
    risk_level: normalizeRiskLevel(record.risk_level, scoreToLevel(record.risk_score || record.overall_risk_score)),
    updated_at: record.updated_at,
  };
}

export function toClimatePayload(form) {
  const blankToNull = (value) => (value === "" || value == null ? null : value);
  return {
    title: (form.title || "").trim(),
    risk_category: form.risk_category || "physical_risks",
    time_horizon: form.time_horizon || "medium_term",
    description: form.description || "",
    likelihood: blankToNull(form.likelihood),
    consequence: blankToNull(form.consequence),
  };
}

export function unwrapList(payload) {
  const items =
    payload?.data ||
    payload?.risk_assessments ||
    payload?.sjas ||
    payload?.climate_risks ||
    (Array.isArray(payload) ? payload : []);
  return {
    items: Array.isArray(items) ? items : [],
    meta: payload?.meta || {},
  };
}

export function unwrapRecord(payload) {
  if (!payload) return null;
  if (payload.data && !Array.isArray(payload.data)) return payload.data;
  return payload;
}

export function riskApiError(error) {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) return data.errors.join(". ");
  return data?.error || data?.message || error?.message || "";
}

export function campaignProgress(campaign = {}) {
  const progress = campaign.progress || {};
  const total = Number(progress.total) || 0;
  const done = (Number(progress.assessed) || 0) + (Number(progress.skipped) || 0);
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return { total, done, pending: Number(progress.pending) || 0, percent };
}

export function campaignYearOptions(from = new Date().getFullYear() - 2, to = new Date().getFullYear() + 2) {
  const years = [];
  for (let year = to; year >= from; year -= 1) years.push(year);
  return years;
}

export function formatDisplayDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
  return parsed.toLocaleDateString();
}

export function levelTone(level) {
  switch (normalizeRiskLevel(level)) {
    case "lav":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case "moderat":
      return "bg-amber-50 text-amber-800 ring-amber-200";
    case "hoy":
      return "bg-orange-50 text-orange-800 ring-orange-200";
    case "kritisk":
      return "bg-rose-50 text-rose-800 ring-rose-200";
    default:
      return "bg-slate-100 text-slate-600 ring-slate-200";
  }
}

export function statusTone(status) {
  switch (normalizeRiskStatus(status)) {
    case "suggested":
      return "bg-violet-50 text-violet-800 ring-violet-200";
    case "assessed":
      return "bg-blue-50 text-blue-800 ring-blue-200";
    case "in_progress":
      return "bg-sky-50 text-sky-800 ring-sky-200";
    case "skipped":
      return "bg-slate-100 text-slate-600 ring-slate-200";
    case "closed":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    default:
      return "bg-slate-50 text-slate-700 ring-slate-200";
  }
}

export function matrixCellTone(score) {
  if (score <= 4) return "bg-emerald-100 text-emerald-900";
  if (score <= 12) return "bg-amber-100 text-amber-900";
  if (score <= 20) return "bg-orange-100 text-orange-900";
  return "bg-rose-100 text-rose-900";
}

export function placeOnMatrix(record) {
  const likelihood = Number(record.likelihood) || levelToScores(record.risk_level).likelihood || 0;
  const consequence =
    Number(record.consequence_score) || levelToScores(record.risk_level).consequence_score || 0;
  return { likelihood, consequence, score: likelihood * consequence };
}

export function activeRiskTab(pathname = "") {
  if (pathname.includes("/matrix")) return "matrix";
  if (pathname.includes("/sja")) return "sja";
  if (pathname.includes("/climate")) return "climate";
  if (pathname.includes("/chemical-inventory")) return "chemical-inventory";
  if (pathname.includes("/protective-equipment")) return "protective-equipment";
  if (pathname.includes("/register")) return "register";
  if (pathname.includes("/campaigns") || pathname === RISK_PATHS.hub) return "campaigns";
  return "campaigns";
}
